import React, { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import {
  acceptStudioInvitation,
  declineStudioInvitation,
  getStudioInvitation
} from '../services/emailApi';

const StudioInvitationModal = ({ invitation, isOpen, onClose }) => {
  const [responding, setResponding] = useState(false);
  const [error, setError] = useState('');
  const [details, setDetails] = useState(invitation || null);
  const token = invitation?.token || invitation?.invitation_token;

  useEffect(() => {
    let active = true;
    const loadInvitation = async () => {
      if (!isOpen || !token) return;
      setError('');
      const result = await getStudioInvitation(token);
      if (!active) return;
      if (result.success) {
        setDetails({ ...result.invitation, token });
      } else {
        setError(result.message || 'This invitation is unavailable.');
      }
    };
    setDetails(invitation || null);
    loadInvitation();
    return () => {
      active = false;
    };
  }, [isOpen, invitation, token]);

  const handleResponse = async (accept) => {
    if (!token) {
      setError('This invitation link is incomplete. Ask your teacher to resend it.');
      return;
    }
    if (details?.status !== 'pending') {
      setError(
        details?.status === 'expired'
          ? 'This invitation has expired. Ask your teacher to resend it.'
          : `This invitation is ${details?.status || 'unavailable'}.`
      );
      return;
    }

    setResponding(true);
    setError('');

    try {
      const result = accept
        ? await acceptStudioInvitation(token)
        : await declineStudioInvitation(token);
      if (result.success) {
        localStorage.removeItem('pending_studio_invitation');
        if (accept && result.studio_id) {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          user.studio_id = result.studio_id;
          localStorage.setItem('user', JSON.stringify(user));
        }
        onClose(accept);
      } else {
        setError(result.message || 'Failed to respond to invitation');
      }
    } catch (err) {
      console.error('Error responding to invitation:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setResponding(false);
    }
  };

  if (!invitation) return null;
  const isPending = details?.status === 'pending';
  const terminalStatuses = new Set([
    'accepted',
    'declined',
    'expired',
    'revoked'
  ]);
  const handleClose = () => {
    if (terminalStatuses.has(details?.status) || !token) {
      localStorage.removeItem('pending_studio_invitation');
    }
    // A wrong-account user can dismiss or sign out without destroying the
    // invitation token needed by the intended account.
    onClose(false);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  <div className="flex items-center">
                    <UserGroupIcon className="h-6 w-6 mr-2 text-indigo-600" />
                    Studio Invitation
                  </div>
                </Dialog.Title>

                <div className="mt-4">
                  <p className="text-sm text-gray-500">
                    You've been invited to join:
                  </p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {details?.studio_name || 'Studio'}
                  </p>
                  {details?.teacher_name && (
                    <p className="text-sm text-gray-600 mt-2">
                      Teacher: {details.teacher_name}
                    </p>
                  )}
                  {details?.recipient_hint && (
                    <p className="text-xs text-gray-500 mt-2">
                      Sent to {details.recipient_hint}. You must be signed in with that email.
                    </p>
                  )}
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-900">
                    Joining this studio gives {details?.teacher_name || 'your teacher'} access to:
                  </p>
                  <ul className="text-sm text-blue-700 mt-2 space-y-1 ml-4">
                    <li>• Your saved practice attempts and results</li>
                    <li>• Your corrections and progress history</li>
                    <li>• Your practice activity and exercise recency</li>
                  </ul>
                  <p className="mt-3 text-xs text-blue-800">
                    This access is automatic while you are a member of the studio. You can leave
                    from Settings, and either you or the teacher can end the membership. Your
                    personal account and practice history are not deleted when access ends.
                  </p>
                </div>

                {error && (
                  <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div className="mt-6 flex space-x-3">
                  <button
                    type="button"
                    className="flex-1 inline-flex justify-center rounded-lg border border-transparent px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500"
                    onClick={() => handleResponse(true)}
                    disabled={responding || !isPending}
                  >
                    {responding ? 'Processing...' : 'Accept and Join Studio'}
                  </button>
                  <button
                    type="button"
                    className="flex-1 inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleResponse(false)}
                    disabled={responding || !isPending}
                  >
                    {responding ? 'Processing...' : 'Decline'}
                  </button>
                </div>
                <button
                  type="button"
                  className="mt-3 w-full rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400"
                  onClick={handleClose}
                  disabled={responding}
                >
                  {isPending ? 'Not now' : 'Close'}
                </button>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default StudioInvitationModal;
